/* eslint-disable camelcase */
import * as React from 'react'
import { ReactElement } from 'react'
import { connect } from 'react-redux'
import { find } from 'lodash'
import { Dispatch } from 'redux'
import { withRouter } from '../../../../../withRouter'
import useFeatureFlags from 'hooks/useFeatureFlags'
import Operations from '../../../../../operations'
import Selectors from '../../../../../selectors'
import {
  ConsentForm,
  Home,
  Resident,
  Carer,
  Log,
  LogCategoryItem
} from '@lmc/ts-common'
import { ConsentStatus } from '@lmc/common/types'

// Import both versions
import { ConsentFormScreen as NewVersion } from './ConsentFormScreenNew'
import { ConsentFormScreen as LegacyVersion } from './ConsentFormScreenLegacy'
const ConsentFormScreenWrapper = (
  props: ConsentFormScreenWrapperProps
): ReactElement => {
  const featureFlags = useFeatureFlags({ flagNames: ['informed_consent'] }).data
  const isInformedConsentEnabled =
    featureFlags['informed_consent']?.enabled || false

  // Return the appropriate version based on feature flag
  if (isInformedConsentEnabled) {
    return (
      <NewVersion {...(props as React.ComponentProps<typeof NewVersion>)} />
    )
  }

  return (
    <LegacyVersion {...(props as React.ComponentProps<typeof LegacyVersion>)} />
  )
}

type StateType = {
  data: {
    'consent-forms': ConsentForm[]
    'log-category-items': LogCategoryItem[]
    residents: Resident[]
    carers: Carer[]
  }
  global: {
    home: Home
  }
}

type OwnProps = {
  router: {
    params: {
      id: string
      consent_form_id: string
    }
  }
}

const mapStateToProps = (state: StateType, props: OwnProps) => {
  const form = find(state?.data['consent-forms'], {
    id: props?.router?.params?.consent_form_id
  })
  // For snapshots, the resident comes from URL params (props.router.params.id)
  // For regular forms, it comes from form.resident
  const residentId = form?.resident || props?.router?.params?.id
  return {
    logs: find(Selectors.getResidentConsentForms(state), {
      id: props?.router?.params?.consent_form_id
    })?.logs,
    consentForm: form,
    home: state?.global?.home,
    signatureItem: find(state.data['log-category-items'], {
      itemId: 'signature'
    }),
    resident: find(state?.data.residents, {
      id: residentId
    }),
    lastReviewCarer: find(state?.data.carers, {
      id: form?.editedBy
    })
  }
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  fetchResidentConsentFormByID: (id: string) => {
    dispatch(Operations.loadListItem('consent-forms', id))
  },
  updateConsent: ({
    isRefused,
    comment,
    editedBy,
    editedOn,
    nextReviewDate,
    consentForm,
    consentStatus,
    carePlans,
    statusReason,
    lastReviewed
  }: UpdateConsentParams) =>
    dispatch(
      Operations.updateListItem({
        listId: 'consent-forms',
        itemId: consentForm.id,
        successMsg: '',
        json: {
          isRefused,
          reviewComment: comment,
          editedBy,
          editedOn,
          nextReviewDate,
          consentStatus,
          carePlans,
          statusReason,
          lastReviewed
        }
      })
    )
})

type ConsentFormScreenWrapperProps = {
  fetchResidentConsentFormByID: (id: string) => void
  consentForm?: ConsentForm
  home: Home
  resident?: Resident
  logs?: Log[]
  signatureItem?: LogCategoryItem
  lastReviewCarer?: Carer
  updateConsent: (params: UpdateConsentParams) => Promise<unknown>
  router: {
    params: {
      id: string
      consent_form_id: string
    }
  }
}

type UpdateConsentParams = {
  isRefused: boolean
  comment?: string
  editedBy: string
  editedOn: string
  nextReviewDate: string | null
  consentForm: ConsentForm
  consentStatus?: ConsentStatus
  carePlans?: string[]
  statusReason?: string
  lastReviewed?: string | null
}

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ConsentFormScreenWrapper)
)
